import { useState, useEffect } from "react";
import { Card, Typography, Button, Row, Col, Progress, Badge, Select } from "antd";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChalkboardTeacher, faChartSimple, faFilePowerpoint, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

export function ProjectCharts({ projectStats, onProjectClick, hidenMenu, ocultarData }) {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    const [selectedProject, setSelectedProject] = useState('');
    const allProjects = Object.values(projectStats || {});
    const [filteredProjects, setFilteredProjects] = useState(allProjects);
    const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedProjects = filteredProjects.slice(startIndex, startIndex + itemsPerPage);
    const chartData = allProjects.map((project, index) => {
        const dataItem = {
            name: project.projectName?.length > 30
                ? project.projectName.substring(0, 30) + "..."
                : project.projectName,
            fullName: project.projectName,
            validados: parseInt(project.validated) || 0,
            sinValidar: parseInt(project.notValidated) || 0,
            sinRegistrar: parseInt(project.notRegistered) || 0,
            total: parseInt(project.total) || 0,
        };
        return dataItem;
    });
    useEffect(() => {
        const newAllProjects = Object.values(projectStats || {});
        setFilteredProjects(newAllProjects);
        setCurrentPage(1);
    }, [projectStats]);

    const handleBarClick = (data) => {
        if (onProjectClick && data?.fullName) {
            onProjectClick(data.fullName)
        }
    }

    const handleSearch = (value) => {
        if (value) {
            const filtered = allProjects.filter(project =>
                project.projectName.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredProjects(filtered);
        } else {
            setFilteredProjects(allProjects);
        }
        setCurrentPage(1);
    };

    const handleProjectSelect = (value) => {
        setSelectedProject(value);
        if (value) {
            const filtered = allProjects.filter(project =>
                project.projectName === value
            );
            setFilteredProjects(filtered);
        } else {
            setFilteredProjects(allProjects);
        }
        setCurrentPage(1);
    };

    return (
        <div>
            <Card
                style={{
                    padding: 10,
                    borderRadius: 5,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    backgroundColor: "#042956",
                    marginBottom: 20,
                }}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={2} style={{ margin: 0, color: "#ffffff" }}>
                            <FontAwesomeIcon onClick={hidenMenu} icon={ocultarData ? faEyeSlash : faEye} style={{ marginRight: 10 }} />
                            <FontAwesomeIcon icon={faChalkboardTeacher} style={{ marginRight: 10 }} />
                            Análisis por Proyectos
                        </Title>
                        <Paragraph type="secondary" style={{ margin: 0, color: "#ffffff" }}>
                            Estado de validación de archivos por proyecto
                        </Paragraph>
                    </Col>
                    <Col>
                        <Badge count={`${filteredProjects.length} proyectos`} style={{ backgroundColor: "#338BC7" }} />
                    </Col>
                </Row>
            </Card>
            <Card
                style={{
                    borderRadius: 5,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        backgroundColor: "#042956",
                        color: "#fff",
                        fontSize: 16,
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        padding: "12px 16px",
                        borderTopLeftRadius: 5,
                        borderTopRightRadius: 5,
                        marginBottom: 16,
                    }}
                >
                    <FontAwesomeIcon icon={faChartSimple} style={{ marginRight: 8 }} />
                    Archivos por Proyecto
                </div>
                <div style={{
                    height: 500,
                    minHeight: 400,
                    padding: '0 10px 10px 0'
                }}>
                    {chartData.length === 0 ? (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column'
                        }}>
                            <Text strong>No hay datos para mostrar la gráfica</Text>
                            <Text type="secondary">Datos recibidos: {allProjects.length} proyectos</Text>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 10, right: 50, left: 0, bottom: 10 }}
                                barSize={20}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                                <XAxis
                                    type="number"
                                    stroke="#666"
                                    fontSize={11}
                                    domain={[0, 'dataMax + 5']}
                                    tickLine={false}
                                    axisLine={{ stroke: '#ddd' }}
                                    tick={{ fill: '#666' }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    stroke="#666"
                                    fontSize={11}
                                    width={240}
                                    interval={0}
                                    tickLine={false}
                                    axisLine={{ stroke: '#ddd' }}
                                    tick={{ fill: '#666' }}
                                    tickMargin={5}
                                />
                                <Tooltip
                                    formatter={(value, name) => [
                                        <span style={{ color: '#333', fontWeight: 'bold' }}>{value}</span>,
                                        name
                                    ]}
                                    labelFormatter={(label) => `Proyecto: ${label}`}
                                    contentStyle={{
                                        borderRadius: '6px',
                                        border: '1px solid #ddd',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Legend
                                    verticalAlign="bottom"
                                    height={40}
                                    wrapperStyle={{
                                        paddingTop: '15px',
                                        paddingLeft: '55px',
                                        paddingBottom: '5px'
                                    }}
                                    iconType="circle"
                                    iconSize={10}
                                />
                                <Bar
                                    dataKey="validados"
                                    name="Validados"
                                    fill="#009728"
                                    cursor="pointer"
                                    stackId="a"
                                    radius={[0, 3, 3, 0]}
                                    onClick={handleBarClick}
                                />
                                <Bar
                                    dataKey="sinValidar"
                                    name="Sin Validar"
                                    fill="#F8C340"
                                    cursor="pointer"
                                    stackId="a"
                                    radius={[0, 3, 3, 0]}
                                    onClick={handleBarClick}
                                />
                                <Bar
                                    dataKey="sinRegistrar"
                                    name="Sin Registrar"
                                    fill="#FF4D4F"
                                    cursor="pointer"
                                    stackId="a"
                                    radius={[0, 3, 3, 0]}
                                    onClick={handleBarClick}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </Card>
            <div style={{ marginTop: 32 }}>
                {filteredProjects.length === 0 ? (
                    <Text type="secondary">No hay proyectos para mostrar</Text>
                ) : (
                    <>
                        <Card
                            style={{
                                padding: 10,
                                borderRadius: 5,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                backgroundColor: "#042956",
                                marginBottom: 20,
                            }}
                        >
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Title level={2} style={{ margin: 0, color: "#ffffff" }}>
                                        <FontAwesomeIcon icon={faFilePowerpoint} style={{ marginRight: 10 }} />
                                        Proyectos ({filteredProjects.length})
                                    </Title>
                                </Col>
                                <Col>
                                    <Select
                                        showSearch
                                        style={{ width: 350 }}
                                        placeholder="Selecciona o busca un proyecto"
                                        optionFilterProp="children"
                                        value={selectedProject}
                                        onChange={handleProjectSelect}
                                        onSearch={handleSearch}
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        allowClear
                                    >
                                        <Option value="">Todos los proyectos</Option>
                                        {allProjects.map(project => (
                                            <Option key={project.projectName} value={project.projectName}>
                                                {project.projectName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                        </Card>
                        {totalPages > 0 && (
                            <div style={{ width: '100%', overflowX: 'hidden' }}>
                                <Row gutter={[16, 16]} style={{ margin: 0 }}>
                                    {paginatedProjects.map((project, index) => {
                                        const validationRate = project.total > 0 ? Math.round((project.validated / project.total) * 100) : 0;
                                        return (
                                            <Col
                                                xs={24}
                                                md={12}
                                                lg={8}
                                                key={project.projectName || index}
                                                style={{ padding: '0 8px' }}
                                            >
                                                <div style={{ height: '100%', display: 'flex' }}>
                                                    <Card
                                                        hoverable
                                                        onClick={() => onProjectClick?.(project.projectName)}
                                                        style={{
                                                            borderColor: "#338BC7",
                                                            borderRadius: 12,
                                                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                                            overflow: "hidden",
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            width: '100%',
                                                            height: '100%',
                                                            margin: '0 auto'
                                                        }}
                                                        styles={{
                                                            body: {
                                                                padding: 0,
                                                                flex: 1,
                                                                display: 'flex',
                                                                flexDirection: 'column'
                                                            }
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                backgroundColor: "#042956",
                                                                color: "#fff",
                                                                fontWeight: "bold",
                                                                fontSize: 16,
                                                                padding: "16px",
                                                                borderTopLeftRadius: 12,
                                                                borderTopRightRadius: 12,
                                                                minHeight: 60,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <Text
                                                                ellipsis={{ tooltip: project.projectName }}
                                                                style={{
                                                                    color: '#fff',
                                                                    margin: 0,
                                                                    lineHeight: '1.3',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    width: '100%'
                                                                }}
                                                            >
                                                                {project.projectName}
                                                            </Text>
                                                        </div>
                                                        <div style={{
                                                            padding: "16px",
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'space-between'
                                                        }}>
                                                            <div>
                                                                <div style={{ marginBottom: 12 }}>
                                                                    <Text type="secondary">Total:</Text>{" "}
                                                                    <Text strong style={{ fontSize: 15 }}>{project.total}</Text>
                                                                </div>
                                                                <div style={{ marginBottom: 16 }}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                                        <Text type="secondary">Validación:</Text>
                                                                        <Text strong>{validationRate}%</Text>
                                                                    </div>
                                                                    <Progress
                                                                        percent={validationRate}
                                                                        showInfo={false}
                                                                        strokeColor="#009728"
                                                                        size="small"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div style={{ marginTop: 'auto' }}>
                                                                <Row justify="space-between" gutter={[8, 8]}>
                                                                    <Col style={{ textAlign: "center", flex: 1 }}>
                                                                        <div style={{ padding: '4px' }}>
                                                                            <Text strong style={{ color: "#009728", fontSize: 16 }}>
                                                                                {project.validated}
                                                                            </Text>
                                                                            <div style={{ fontSize: 12, color: '#666' }}>Valid.</div>
                                                                        </div>
                                                                    </Col>
                                                                    <Col style={{ textAlign: "center", flex: 1 }}>
                                                                        <div style={{ padding: '4px' }}>
                                                                            <Text strong style={{ color: "#F8C340", fontSize: 16 }}>
                                                                                {project.notValidated}
                                                                            </Text>
                                                                            <div style={{ fontSize: 12, color: '#666' }}>Pend.</div>
                                                                        </div>
                                                                    </Col>
                                                                    <Col style={{ textAlign: "center", flex: 1 }}>
                                                                        <div style={{ padding: '4px' }}>
                                                                            <Text strong style={{ color: "#0869C6", fontSize: 16 }}>
                                                                                {project.notRegistered}
                                                                            </Text>
                                                                            <div style={{ fontSize: 12, color: '#666' }}>N/R</div>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        )}
                    </>
                )}
            </div>
            {totalPages > 1 && (
                <Row justify="center" style={{ marginTop: 20 }}>
                    <Button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        style={{ marginRight: 8 }}
                    >
                        Anterior
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <Button
                            key={page}
                            type={currentPage === page ? "primary" : "default"}
                            onClick={() => setCurrentPage(page)}
                            style={{ margin: "0 4px" }}
                        >
                            {page}
                        </Button>
                    ))}
                    <Button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        style={{ marginLeft: 8 }}
                    >
                        Siguiente
                    </Button>
                </Row>
            )}
        </div>
    );
}